/**
 * Wally Collection Engagement
 * Tracks product impressions and clicks on collection pages.
 * Loads async with defer, uses IntersectionObserver and sendBeacon.
 *
 * Required data attributes on product cards:
 *   data-product-id="123456789"  (Shopify numeric product ID)
 *   data-position="0"  (0-indexed position in the collection grid)
 *
 * Required data attribute on the collection container:
 *   data-collection="new-in"  (collection handle)
 *
 * Config (set before this script loads):
 *   window.__wEp = 'https://endpoint/events';
 */

(function () {
  'use strict';

  var ENDPOINT = window.__wEp;
  if (!ENDPOINT) return;

  var FLUSH_TIMEOUT = 30000; // 30 seconds
  var IMPRESSION_THRESHOLD = 0.5; // 50% visible

  // Collection handle from container
  var container = document.querySelector('[data-collection]');
  if (!container) return;
  var collectionHandle = container.getAttribute('data-collection');
  if (!collectionHandle) return;

  // Session ID (persists across page navigations within same session)
  var sessionId = sessionStorage.getItem('wally_sid');
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    sessionStorage.setItem('wally_sid', sessionId);
  }

  var eventQueue = [];
  var impressedProducts = {}; // track which products already had an impression
  var flushTimer = null;

  function queueEvent(type, productId, position) {
    eventQueue.push({
      type: type,
      product_id: productId,
      collection: collectionHandle,
      position: typeof position === 'string' ? parseInt(position, 10) : position,
      timestamp: Math.floor(Date.now() / 1000),
    });
    resetFlushTimer();
  }

  function flush() {
    if (eventQueue.length === 0) return;

    var payload = JSON.stringify({
      events: eventQueue.splice(0),
      session_id: sessionId,
      page_url: location.pathname,
    });

    // sendBeacon with text/plain avoids CORS preflight, critical for
    // click events that flush during page navigation. application/json
    // triggers a preflight OPTIONS request that can't complete before
    // the page unloads, causing the POST to be silently dropped.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, payload);
    } else {
      fetch(ENDPOINT, {
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
      }).catch(function () {});
    }
  }

  function resetFlushTimer() {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, FLUSH_TIMEOUT);
  }

  // --- Impression tracking via IntersectionObserver ---

  var productCards = document.querySelectorAll('[data-product-id]');
  if (productCards.length === 0) return;

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        var card = entry.target;
        var productId = card.getAttribute('data-product-id');
        if (impressedProducts[productId]) continue;

        impressedProducts[productId] = true;
        var position = card.getAttribute('data-position');
        queueEvent('impression', productId, position);

        // Stop observing once impressed
        observer.unobserve(card);
      }
    },
    { threshold: IMPRESSION_THRESHOLD }
  );

  for (var i = 0; i < productCards.length; i++) {
    observer.observe(productCards[i]);
  }

  // --- Click tracking via event delegation ---
  // Horizon theme uses a product-card web component that intercepts clicks
  // and navigates via JS. Clicks often land outside the <a> tag, so we
  // find the closest [data-product-id] element directly from the click target.

  // Use mousedown instead of click: Horizon's <product-card> web component
  // intercepts click events via its component framework (on:click) and View
  // Transitions, preventing our capture-phase click handler from working.
  // mousedown fires before all of that and reliably captures user intent.
  container.addEventListener('mousedown', function (e) {
    // Only track left-clicks (ignore right-click, middle-click)
    if (e.button !== 0) return;
    // Skip clicks on interactive elements (variant picker, quick add, etc.)
    if (e.target.closest('button, input, label, select')) return;

    // Find the product card from the click target
    var card = e.target.closest('[data-product-id]');
    if (!card) return;

    // Only count if the card is a direct child of the collection grid (not nested components)
    var gridItem = card.closest('li[data-product-id]') || card.closest('[data-product-id][data-position]');
    if (!gridItem) return;

    var productId = gridItem.getAttribute('data-product-id');
    var position = gridItem.getAttribute('data-position');
    queueEvent('click', productId, position);

    // Inject collection context into the product card link for PDP tracking.
    // Must happen before <product-card> reads the href for navigation.
    var link = gridItem.querySelector('a[href*="/products/"]');
    if (link) {
      var href = link.getAttribute('href');
      if (href && href.indexOf('ref=') === -1) {
        var separator = href.indexOf('?') === -1 ? '?' : '&';
        link.setAttribute('href', href + separator + 'ref=' + collectionHandle + '&pos=' + (position || 0));
      }
    }

    // Flush immediately on click (user is navigating away)
    flush();
  }, true);

  // --- GA4 bonus events ---

  function fireGA4ViewItemList() {
    if (typeof gtag !== 'function') return;

    var items = [];
    var visible = document.querySelectorAll('[data-product-id]');
    for (var i = 0; i < visible.length; i++) {
      var card = visible[i];
      items.push({
        item_id: card.getAttribute('data-product-id'),
        item_name: card.getAttribute('data-product-title') || '',
        item_brand: card.getAttribute('data-product-vendor') || '',
        item_category: card.getAttribute('data-product-type') || '',
        price: parseFloat(card.getAttribute('data-product-price')) || 0,
        index: parseInt(card.getAttribute('data-position'), 10) || i,
        item_list_name: collectionHandle,
        item_list_id: collectionHandle,
      });
    }

    if (items.length > 0) {
      gtag('event', 'view_item_list', {
        item_list_id: collectionHandle,
        item_list_name: collectionHandle,
        items: items,
      });
    }
  }

  // Fire GA4 view_item_list once on load
  if (document.readyState === 'complete') {
    fireGA4ViewItemList();
  } else {
    window.addEventListener('load', fireGA4ViewItemList);
  }

  // --- Flush on page hide ---

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flush();
  });

  // Fallback for older browsers
  window.addEventListener('pagehide', flush);
})();
